<template>
  <b-card no-body>
    <b-card-header>
      <h5 class="mb-0 text-primary">{{ isEdit ? "Editar" : "Crear" }} Tipo de Inventario</h5>
    </b-card-header>
    
    <b-card-body>
      <el-form :model="form" ref="form" label-position="top" class="demo-ruleForm">
        
        <!-- Basic Info Row -->
        <b-row>
            <b-col md="6">
                 <el-form-item
                  label="Nombre"
                  prop="name"
                  :rules="[{ required: true, message: 'El nombre es obligatorio', trigger: 'blur' }]"
                >
                    <el-input v-model="form.name" placeholder="Ingrese el nombre"></el-input>
                </el-form-item>
            </b-col>
            <b-col md="6">
                <el-form-item label="Países" prop="countries_ids">
                  <el-select
                    v-model="form.countries_ids"
                    multiple
                    filterable
                    placeholder="Seleccione los países"
                    style="width: 100%"
                  >
                    <el-option
                      v-for="country in countriesList"
                      :key="country.id"
                      :label="country.name"
                      :value="country.id"
                    ></el-option>
                  </el-select>
                </el-form-item>
            </b-col>
        </b-row>

        <hr />

        <!-- Info Message and Actions Row -->
        <b-row class="mb-3 align-items-center">
             <b-col md="8">
                 <b-alert show variant="info" class="mb-0 py-2 px-3 bg-white border-left-info shadow-sm">
                    <div class="d-flex align-items-center">
                        <i class="fa fa-info-circle fa-lg text-info mr-3"></i>
                        <div class="small text-dark">
                            <span class="d-block mb-1">
                                <strong>Nota:</strong> <strong>DATOS GENERALES</strong> y <strong>Colombia</strong> se incluyen automáticamente.
                            </span>
                            <span class="text-muted">
                                <i class="fa fa-arrows-v text-secondary"></i> Arrastre los encabezados para reordenar.
                            </span>
                        </div>
                    </div>
                 </b-alert>
             </b-col>
             <b-col md="4" class="text-right">
                  <el-dropdown @command="handleAddSection" trigger="click">
                     <b-button variant="primary" class="shadow-sm">
                       <i class="fa fa-plus"></i> Agregar Sección
                     </b-button>
                    <el-dropdown-menu slot="dropdown" class="section-dropdown">
                      <el-dropdown-item
                        v-for="section in availableSections"
                        :key="section.id"
                        :command="section.id"
                      >
                        {{ section.name }}
                      </el-dropdown-item>
                      <el-dropdown-item v-if="availableSections.length === 0" disabled>
                        Todas las secciones han sido agregadas
                      </el-dropdown-item>
                    </el-dropdown-menu>
                  </el-dropdown>
             </b-col>
        </b-row>

        <!-- Sections List -->
        <div class="section-list">
          <draggable v-model="sectionItems" handle=".card-header" ghost-class="ghost">
            <transition-group type="transition" name="flip-list">
              <b-card
                v-for="(item, index) in sectionItems"
                :key="item.id"
                no-body
                class="mb-2 section-card border shadow-sm"
              >
                <!-- Card Header -->
                <div class="card-header bg-white d-flex justify-content-between align-items-center py-2 px-3 cursor-grab" style="border-bottom: 1px solid #f0f0f0;">
                   <div class="d-flex align-items-center flex-grow-1" @click="toggleSection(index)">
                      <div class="mr-3 text-muted drag-icon">
                          <i class="fa fa-bars"></i>
                      </div>
                      <span class="badge badge-light border mr-2 text-muted font-weight-normal" style="min-width: 25px;">{{ index + 1 }}</span>
                      <strong class="text-dark">{{ item.name }}</strong>
                      
                      <span class="ml-3 text-muted small" v-if="!item.isOpen">
                          <i class="fa fa-columns"></i> {{ item.selectedColumns.length }} columnas seleccionadas
                      </span>
                   </div>
                   
                   <div class="d-flex align-items-center">
                      <!-- Toggle Button -->
                      <b-button variant="link" class="text-muted p-0 mr-3" @click.stop="toggleSection(index)" title="Expandir/Contraer">
                          <i :class="item.isOpen ? 'fa fa-chevron-up' : 'fa fa-chevron-down'"></i>
                      </b-button>

                      <!-- Delete Button -->
                      <b-button variant="link" class="text-danger p-0" @click.stop="removeSection(index)" @mousedown.stop title="Eliminar sección">
                          <i class="fa fa-times"></i>
                      </b-button>
                   </div>
                </div>
                
                <!-- Card Body (Collapsible) -->
                <b-collapse v-model="item.isOpen" :id="'collapse-' + item.id">
                    <div class="card-body py-3 bg-light-section">
                       <p class="text-muted small mb-2 font-weight-bold ml-1 text-uppercase">Selección de Columnas</p>
                       
                       <div v-if="item.allColumns && item.allColumns.length > 0">
                           <el-checkbox-group v-model="item.selectedColumns">
                              <b-row class="mx-0">
                                  <b-col cols="12" sm="6" md="4" lg="3" v-for="col in item.allColumns" :key="col.id" class="mb-1 px-1">
                                      <div class="text-truncate custom-checkbox-wrapper" :title="col.name">
                                          <el-checkbox :label="col.id">{{ col.name }}</el-checkbox>
                                      </div>
                                  </b-col>
                              </b-row>
                           </el-checkbox-group>
                       </div>
                       <div v-else class="text-warning small px-2">
                           <i class="fa fa-exclamation-triangle"></i> Esta sección no tiene columnas disponibles.
                       </div>
                    </div>
                </b-collapse>
              </b-card>
            </transition-group>
          </draggable>
          
           <!-- Empty State -->
            <div v-if="sectionItems.length === 0" class="text-center p-4 border dashed-border rounded bg-light mt-2">
               <div class="text-muted">
                  <p class="mb-0"><i class="fa fa-plus-circle mr-1"></i> Agregue secciones para configurar el formulario.</p>
               </div>
            </div>
        </div>

        <div class="d-flex justify-content-end mt-4 pt-3 border-top">
           <b-button variant="secondary" class="mr-2" @click="$emit('cancel')">Cancelar</b-button>
           <b-button variant="primary" :disabled="loading" @click="submitForm">
             <i class="fa fa-save mr-1" v-if="!loading"></i>
             <span v-if="loading" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
             {{ isEdit ? "Actualizar" : "Guardar" }}
           </b-button>
        </div>
      </el-form>
    </b-card-body>
  </b-card>
</template>

<script>
import { mapActions, mapState } from "vuex";
import draggable from "vuedraggable";

export default {
  components: {
    draggable,
  },
  props: {
    id: {
      type: [Number, String],
      default: null,
    },
    initialData: {
      type: Object,
      default: null,
    },
    countriesList: {
      type: Array,
      default: () => [],
    },
    allSectionsList: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      loading: false,
      form: {
        name: "",
        countries_ids: [],
      },
      sectionItems: [], // { id, name, selectedColumns: [], allColumns: [], isOpen: boolean }
    };
  },
  computed: {
    isEdit() {
      return !!this.initialData;
    },
    availableSections() {
      const addedIds = this.sectionItems.map((s) => s.id);
      return (this.allSectionsList || []).filter((s) => !addedIds.includes(s.id));
    },
  },
  watch: {
    initialData: {
        immediate: true,
        handler(val) {
            if (val) {
                this.assignData(val);
            } else {
                this.resetForm();
            }
        }
    }
  },
  methods: {
    ...mapActions("sections", ["getSectionById"]),

    toggleSection(index) {
        // Toggle specific section
        this.$set(this.sectionItems[index], 'isOpen', !this.sectionItems[index].isOpen);
    },

    resetForm() {
        this.form.name = "";
        this.form.countries_ids = [];
        this.sectionItems = [];
    },

    async assignData(data) {
      if (data) {
        this.form.name = data.name;
        this.form.countries_ids = data.countries 
          ? data.countries
              .filter(c => c.code !== 'CO' && c.name !== 'Colombia')
              .map(c => c.id) 
          : [];
        
        this.sectionItems = [];
        if (data.inventory_columns) {
            // Reconstruct sections from flat columns
            // Group by section Id
            const sectionsMap = {};
            
            data.inventory_columns.forEach(col => {
                // Filter out fixed sections
                const sectionCode = col.inventory_section ? col.inventory_section.code : '';
                if (['DAT_GENERALES', 'UBICACIONES'].includes(sectionCode)) return;

                const sId = col.slq_inventory_section_id;
                // This assumes we have access to section order somehow, ideally from backend
                // If not, we rely on the order they appear or defaults
                const sOrder = col.pivot ? col.pivot.order_section : 999; 
                
                if (!sectionsMap[sId]) {
                    sectionsMap[sId] = {
                        id: sId,
                        order: sOrder,
                        selectedCols: [],
                        name: col.inventory_section ? col.inventory_section.name : 'Sección Desconocida',
                        isOpen: false // Default collapsed on load
                    };
                }
                sectionsMap[sId].selectedCols.push(col.id);
            });
            
            // Sort by order
            const sortedSections = Object.values(sectionsMap).sort((a,b) => a.order - b.order);
            
            // Populate UI items
            for (const section of sortedSections) {
                // Ensure we have full section data (columns etc)
                // We use the ID to check against loaded allSectionsList or fetch if needed
                await this.handleAddSection(section.id, section.selectedCols, section.isOpen);
            }
        }
      }
    },

    async handleAddSection(sectionId, preSelectedColumns = [], isOpen = true) {
        if (this.sectionItems.find(s => s.id === sectionId) && preSelectedColumns.length === 0) return;

        // Find in store to avoid network if possible, or use getSectionById
        let sectionData = (this.allSectionsList || []).find(s => s.id === sectionId);
        
        if (!sectionData || !sectionData.inventory_columns) {
            // Fetch if missing or missing columns data
             sectionData = await this.getSectionById(sectionId);
        }

        if (sectionData) {
            this.sectionItems.push({
                id: sectionData.id,
                name: sectionData.name,
                allColumns: sectionData.inventory_columns || [],
                selectedColumns: preSelectedColumns.length > 0 ? preSelectedColumns : (sectionData.inventory_columns || []).map(c => c.id),
                isOpen: isOpen
            });
        }
    },

    removeSection(index) {
      this.sectionItems.splice(index, 1);
    },

    moveSection(index, direction) {
      const newIndex = index + direction;
      if (newIndex >= 0 && newIndex < this.sectionItems.length) {
        const item = this.sectionItems.splice(index, 1)[0];
        this.sectionItems.splice(newIndex, 0, item);
      }
    },

    async submitForm() {
      this.$refs.form.validate((valid) => {
        if (valid) {
          const inventory_sections = this.sectionItems.map((item, index) => ({
            id: item.id,
            order: index + 1,
            columns: item.selectedColumns
          }));

          const payload = {
            ...this.form,
            inventory_sections
          };
          
          this.$emit("submit", payload);
        }
      });
    },
  },
};
</script>

<style scoped>
.section-dropdown {
    max-height: 300px;
    overflow-y: auto;
}
.section-list {
    max-height: 60vh;
    overflow-y: auto;
    padding-right: 5px;
}
.section-card {
    transition: box-shadow 0.2s;
}
.section-card:hover {
    box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
}
.flip-list-move {
  transition: transform 0.5s;
}
.ghost {
  opacity: 0.5;
  background: #c8ebfb;
}
.dashed-border {
    border-style: dashed !important;
    border-width: 2px !important;
    border-color: #dee2e6 !important;
}
.bg-light-info {
    background-color: #e3f2fd !important;
}
.bg-light-section {
    background-color: #f8f9fa;
}
.custom-checkbox-wrapper {
    margin-bottom: 2px;
}
.border-left-info {
    border-left: 4px solid #17a2b8 !important;
}
.cursor-grab {
    cursor: grab;
}
.cursor-grab:active {
    cursor: grabbing;
}
</style>
